import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { paymentsAPI } from "../lib/api";
import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Check, Users, Camera, X,
  LineChart, FileText, MessageSquare, Share2, Sparkles,
  Dumbbell, Zap, Clock
} from "lucide-react";

const plans = [
  {
    name: "Starter",
    price: "Free",
    originalPrice: null,
    period: "forever",
    tagline: "Start managing your first 5 clients for free",
    subtitle: "Perfect to get started",
    badge: null,
    ring: "ring-border",
    accent: "text-muted-foreground",
    btnClass: "bg-white/10 hover:bg-white/20 text-foreground font-bold",
    btnText: "Current Plan",
    btnDisabled: true,
    clients: "5",
    features: [
      { text: "Full client profile management", included: true },
      { text: "Before/After photo gallery", included: true },
      { text: "Weight & measurement tracking", included: true },
      { text: "Progress charts & graphs", included: true },
      { text: "Weekly workout plans", included: true },
      { text: "Client portal (magic links)", included: true },
      { text: "Basic analytics", included: true },
      { text: "PDF reports", included: false },
      { text: "Diet plan management", included: false },
    ],
  },
  {
    name: "Pro",
    price: "599",
    originalPrice: "799",
    discount: "25% OFF",
    period: "/month",
    tagline: "Handle up to 50 clients without chaos",
    subtitle: "Perfect for growing trainers",
    badge: "Most Popular",
    ring: "ring-primary ring-2 shadow-lg shadow-primary/20",
    accent: "text-primary",
    btnClass: "btn-premium text-white font-extrabold",
    btnText: "Go Pro & Remove Limits",
    btnDisabled: false,
    clients: "50",
    features: [
      { text: "Everything in Starter", included: true },
      { text: "Up to 50 clients", included: true },
      { text: "Advanced analytics dashboard", included: true },
      { text: "PDF progress reports", included: true },
      { text: "Diet plan management", included: true },
      { text: "Bulk client import (CSV)", included: true },
      { text: "Email support", included: true },
    ],
  },
  {
    name: "Elite",
    price: "999",
    originalPrice: "1499",
    discount: "33% OFF",
    period: "/month",
    tagline: "Scale your coaching business to 200+ clients",
    subtitle: "Built for serious income growth",
    badge: "Best Value",
    ring: "ring-amber-500/50",
    accent: "text-amber-400",
    btnClass: "bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-extrabold",
    btnText: "Go Elite",
    btnDisabled: false,
    clients: "200",
    features: [
      { text: "Everything in Pro", included: true },
      { text: "Up to 200 clients", included: true },
      { text: "Push notifications", included: true },
      { text: "Client attendance tracking", included: true },
      { text: "Revenue & payment tracking", included: true },
      { text: "Priority support", included: true },
      { text: "Dedicated account manager", included: true },
    ],
  },
];

const comparisonRows = [
  { label: "Price", starter: "₹0", pro: "₹599/mo", elite: "₹999/mo", isPrice: true, starterOrig: null, proOrig: "₹799", eliteOrig: "₹1,499" },
  { label: "Clients", starter: "5", pro: "50", elite: "200" },
  { label: "Client Profiles & Tracking", starter: true, pro: true, elite: true },
  { label: "Before/After Photos", starter: true, pro: true, elite: true },
  { label: "Progress Charts", starter: true, pro: true, elite: true },
  { label: "Weekly Workout Plans", starter: true, pro: true, elite: true },
  { label: "Client Portal (Magic Links)", starter: true, pro: true, elite: true },
  { label: "Analytics", starter: "Basic", pro: "Advanced", elite: "Advanced" },
  { label: "Payment Tracking", starter: "Basic", pro: true, elite: true },
  { label: "PDF Reports", starter: false, pro: true, elite: true },
  { label: "Diet Plan Management", starter: false, pro: true, elite: true },
  { label: "Bulk CSV Import", starter: false, pro: true, elite: true },
  { label: "Push Notifications", starter: false, pro: false, elite: true },
  { label: "Attendance Tracking", starter: false, pro: true, elite: true },
  { label: "Email Support", starter: false, pro: true, elite: true },
  { label: "Priority Support", starter: false, pro: false, elite: true },
  { label: "Dedicated Account Manager", starter: false, pro: false, elite: true },
];

const results = [
  { icon: Users, label: "Track Every Client", desc: "Manage all your clients in one place — save hours every week" },
  { icon: Camera, label: "Before/After Gallery", desc: "Show client transformation with photo timelines" },
  { icon: LineChart, label: "Progress Charts", desc: "See progress clearly — motivate clients with visual results" },
  { icon: Dumbbell, label: "Workout Plans", desc: "Create weekly workout plans for every client" },
  { icon: FileText, label: "PDF Reports", desc: "Generate professional progress reports in one click", pro: true },
  { icon: Share2, label: "Diet Plans", desc: "Create complete diet plans for every client", pro: true },
  { icon: MessageSquare, label: "Support", desc: "support@fittrackcoach.in", isEmail: true },
];

function ComparisonCell({ value, original }) {
  if (value === "soon") {
    return (
      <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20">
        <Clock className="w-3 h-3 text-amber-400" />
        <span className="text-[10px] font-bold text-amber-400 uppercase">Soon</span>
      </div>
    );
  }
  if (typeof value === "boolean") {
    return value ? (
      <div className="w-6 h-6 rounded bg-green-500/20 flex items-center justify-center mx-auto">
        <Check className="w-4 h-4 text-green-400" />
      </div>
    ) : (
      <div className="w-6 h-6 rounded bg-red-500/10 flex items-center justify-center mx-auto">
        <X className="w-4 h-4 text-red-400/70" />
      </div>
    );
  }
  if (original) {
    return (
      <div className="flex flex-col items-center gap-0.5">
        <span className="text-muted-foreground/50 line-through text-xs">{original}</span>
        <span className="font-bold text-foreground">{value}</span>
      </div>
    );
  }
  return <span className="font-semibold text-foreground">{value}</span>;
}

export default function PricingPage() {
  const navigate = useNavigate();
  const { trainer, refreshTrainer } = useAuth();
  const [payingPlan, setPayingPlan] = useState(null);
  const [proDuration, setProDuration] = useState(1);
  const [eliteDuration, setEliteDuration] = useState(1);

  const DURATION_OPTIONS = [
    { months: 1, label: "1 Month" },
    { months: 3, label: "3 Months", save: "Save ~15%" },
    { months: 6, label: "6 Months", save: "Save ~25%" },
  ];

  const PLAN_PRICES = {
    pro: { 1: 599, 3: 1499, 6: 2699 },
    elite: { 1: 999, 3: 2499, 6: 4999 },
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]')) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleUpgrade = async (planKey) => {
    if (trainer?.plan_type === planKey) {
      toast.info("You're already on this plan!");
      return;
    }
    setPayingPlan(planKey);
    try {
      const loaded = await loadRazorpayScript();
      if (!loaded) { toast.error("Payment gateway failed to load"); setPayingPlan(null); return; }

      const { data } = await paymentsAPI.createOrder(planKey);

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "FitTrack Coach",
        description: data.plan_name,
        order_id: data.order_id,
        handler: async (response) => {
          try {
            const verifyRes = await paymentsAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan: planKey,
            });
            toast.success(verifyRes.data.message || "Plan upgraded successfully!");
            await refreshTrainer();
          } catch (err) {
            toast.error(err.response?.data?.detail || "Payment verification failed");
          }
          setPayingPlan(null);
        },
        modal: { ondismiss: () => setPayingPlan(null) },
        prefill: {
          name: trainer?.name || "",
          email: trainer?.email || "",
          contact: trainer?.phone_number ? `+91${trainer.phone_number}` : "",
        },
        theme: { color: "#00BFFF" },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) => {
        toast.error(resp.error?.description || "Payment failed. Please try again.");
        setPayingPlan(null);
      });
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
      setPayingPlan(null);
    }
  };

  const currentPlan = trainer?.plan_type || "beta";

  return (
    <div className="min-h-screen pb-8" data-testid="pricing-page">
      <Header title="Pricing" subtitle="Plans & Pricing" />

      <main className="container mx-auto px-4 py-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <Badge className="bg-primary/10 text-primary border-primary/30 mb-4">
            Only first 50 coaches get lifetime discount
          </Badge>
          <h2 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight uppercase text-foreground mb-3" data-testid="pricing-hero-title">
            Choose Your Plan
          </h2>
          <p className="text-muted-foreground max-w-lg mx-auto font-medium" data-testid="pricing-hero-desc">
            Manage all your clients in one place — <span className="text-primary font-bold">save hours every week</span>
          </p>
        </div>

        {/* 3-Tier Plan Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
          {plans.map((plan) => (
            <Card
              key={plan.name}
              className={`glass-card border-0 relative ring-1 ${plan.ring}`}
              data-testid={`plan-${plan.name.toLowerCase()}`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <Badge className={`${plan.name === 'Pro' ? 'bg-primary/10 text-primary border-primary/30' : 'bg-amber-500/10 text-amber-400 border-amber-500/30'} border px-3 py-1 font-bold`}>
                    {plan.badge}
                  </Badge>
                </div>
              )}

              <CardHeader className="text-center pb-2 pt-8">
                <CardTitle className={`font-heading text-xl tracking-tight uppercase font-extrabold ${plan.accent}`}>
                  {plan.name}
                </CardTitle>
                <div className="mt-3">
                  {plan.price === "Free" ? (
                    <span className="font-heading text-4xl font-extrabold text-foreground">Free</span>
                  ) : (
                    <div className="flex flex-col items-center">
                      {plan.originalPrice && (
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm text-muted-foreground/60 line-through">₹{plan.originalPrice}</span>
                          <Badge className="bg-green-500/10 text-green-400 border-green-500/30 text-[10px] px-1.5 py-0">{plan.discount}</Badge>
                        </div>
                      )}
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-lg text-muted-foreground">₹</span>
                        <span className="font-heading text-4xl font-extrabold text-foreground">{plan.price}</span>
                        <span className="text-sm text-muted-foreground">{plan.period}</span>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-2 font-bold" data-testid={`plan-${plan.name.toLowerCase()}-tagline`}>
                  {plan.tagline}
                </p>
                <p className="text-xs text-muted-foreground/70 mt-1">{plan.subtitle}</p>
              </CardHeader>

              <CardContent className="pt-4">
                <div className="text-center mb-4 p-3 bg-white/[0.03] rounded-xl">
                  <span className={`font-extrabold text-lg ${plan.accent}`}>{plan.clients}</span>
                  <span className="text-sm text-muted-foreground ml-1">clients</span>
                </div>

                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((f) => (
                    <li key={f.text} className="flex items-start gap-2.5 text-sm">
                      {f.included ? (
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${f.soon ? 'bg-amber-500/10 text-amber-400' : 'bg-neon-green/10 text-neon-green'}`}>
                          {f.soon ? <Clock className="w-2.5 h-2.5" /> : <Check className="w-2.5 h-2.5" />}
                        </div>
                      ) : (
                        <div className="w-4 h-4 rounded-full flex items-center justify-center bg-white/5 text-muted-foreground/40 shrink-0 mt-0.5">
                          <X className="w-2.5 h-2.5" />
                        </div>
                      )}
                      <span className={f.included ? "text-foreground" : "text-muted-foreground/50"}>
                        {f.text}
                        {f.soon && <span className="ml-1.5 text-[10px] font-bold text-amber-400 uppercase">Coming Soon</span>}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Duration selector for Pro/Elite */}
                {plan.name !== "Starter" && currentPlan !== plan.name.toLowerCase() && currentPlan !== "elite" && (
                  <div className="flex gap-1 mb-3">
                    {DURATION_OPTIONS.map((d) => {
                      const dur = plan.name === "Pro" ? proDuration : eliteDuration;
                      const setDur = plan.name === "Pro" ? setProDuration : setEliteDuration;
                      const isSelected = dur === d.months;
                      return (
                        <button key={d.months} type="button"
                          onClick={() => setDur(d.months)}
                          className={`flex-1 py-2 px-1 rounded-lg text-center text-[10px] font-bold uppercase transition-all ${
                            isSelected
                              ? "bg-primary/20 text-primary border border-primary/30"
                              : "bg-white/[0.03] text-muted-foreground border border-white/[0.06] hover:bg-white/[0.06]"
                          }`}
                          data-testid={`plan-${plan.name.toLowerCase()}-${d.months}m`}
                        >
                          <div>{d.label}</div>
                          {d.save && <div className="text-green-400 text-[9px] mt-0.5">{d.save}</div>}
                          <div className="mt-0.5">₹{PLAN_PRICES[plan.name.toLowerCase()]?.[d.months]}</div>
                        </button>
                      );
                    })}
                  </div>
                )}

                <Button
                  className={`w-full h-12 tracking-wide uppercase rounded-xl ${
                    plan.name === "Starter"
                      ? (currentPlan === "beta" || currentPlan === "free" ? "bg-white/10 hover:bg-white/20 text-foreground font-bold" : "bg-white/10 text-foreground font-bold")
                      : plan.name === "Pro"
                        ? (currentPlan === "pro" ? "bg-green-500/20 text-green-400 font-bold" : plan.btnClass)
                        : (currentPlan === "elite" ? "bg-green-500/20 text-green-400 font-bold" : plan.btnClass)
                  }`}
                  disabled={
                    plan.name === "Starter"
                      ? true
                      : plan.name === "Pro"
                        ? currentPlan === "pro" || currentPlan === "elite" || payingPlan
                        : currentPlan === "elite" || payingPlan
                  }
                  onClick={() => {
                    const dur = plan.name === "Pro" ? proDuration : eliteDuration;
                    const planKey = `${plan.name.toLowerCase()}_${dur}`;
                    handleUpgrade(planKey);
                  }}
                  data-testid={`plan-${plan.name.toLowerCase()}-btn`}
                >
                  {plan.name === "Starter"
                    ? (currentPlan === "beta" || currentPlan === "free" ? "Current Plan" : "Starter Plan")
                    : plan.name === "Pro"
                      ? (payingPlan ? <div className="spinner" /> : currentPlan === "pro" ? "Current Plan" : currentPlan === "elite" ? "Downgrade N/A" : "Go Pro & Remove Limits")
                      : (payingPlan ? <div className="spinner" /> : currentPlan === "elite" ? "Current Plan" : "Go Elite")
                  }
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* ===== COMPARISON TABLE ===== */}
        <div className="max-w-4xl mx-auto mb-16" data-testid="comparison-table-section">
          <h3 className="font-heading text-2xl font-extrabold tracking-tight uppercase text-foreground text-center mb-2">
            Compare Plans
          </h3>
          <p className="text-center text-muted-foreground mb-8 font-medium">
            See exactly what you get with each plan
          </p>

          <div className="glass-card rounded-2xl overflow-hidden border border-white/[0.06]">
            {/* Table Header */}
            <div className="grid grid-cols-4 bg-white/[0.04] border-b border-white/[0.06]">
              <div className="p-4 text-sm font-bold text-muted-foreground uppercase tracking-wider">Feature</div>
              <div className="p-4 text-center text-sm font-bold text-muted-foreground uppercase tracking-wider">Starter</div>
              <div className="p-4 text-center relative">
                <span className="text-sm font-bold text-primary uppercase tracking-wider">Pro</span>
                <div className="absolute -top-0 left-1/2 -translate-x-1/2 w-16 h-0.5 bg-primary rounded-full" />
              </div>
              <div className="p-4 text-center text-sm font-bold text-amber-400 uppercase tracking-wider">Elite</div>
            </div>

            {/* Table Rows */}
            {comparisonRows.map((row, idx) => (
              <div
                key={row.label}
                className={`grid grid-cols-4 items-center ${idx < comparisonRows.length - 1 ? 'border-b border-white/[0.04]' : ''} ${idx % 2 === 0 ? '' : 'bg-white/[0.01]'}`}
                data-testid={`comparison-row-${row.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="p-4 text-sm font-semibold text-foreground">{row.label}</div>
                <div className="p-4 text-center text-sm">
                  <ComparisonCell value={row.starter} original={row.isPrice ? row.starterOrig : null} />
                </div>
                <div className="p-4 text-center text-sm bg-primary/[0.02]">
                  <ComparisonCell value={row.pro} original={row.isPrice ? row.proOrig : null} />
                </div>
                <div className="p-4 text-center text-sm">
                  <ComparisonCell value={row.elite} original={row.isPrice ? row.eliteOrig : null} />
                </div>
              </div>
            ))}
          </div>

          {/* Coming Soon Legend */}
          <div className="flex items-center justify-center gap-6 mt-4 text-xs text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded bg-green-500/20 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-green-400" /></div>
              <span>Available</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/10 border border-amber-500/20">
                <Clock className="w-2.5 h-2.5 text-amber-400" />
              </div>
              <span>Coming Soon</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 rounded bg-red-500/10 flex items-center justify-center"><X className="w-2.5 h-2.5 text-red-400/70" /></div>
              <span>Not Included</span>
            </div>
          </div>
        </div>

        {/* Results-Focused Features Section */}
        <div className="max-w-4xl mx-auto">
          <h3 className="font-heading text-2xl font-extrabold tracking-tight uppercase text-foreground text-center mb-2">
            What You Get
          </h3>
          <p className="text-center text-muted-foreground mb-8 font-medium">
            Tools that help you grow your coaching business
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {results.map((r) => (
              <div key={r.label} className="text-center p-4 relative">
                {(r.pro || r.elite) && (
                  <Badge className={`absolute -top-1 right-0 text-[9px] px-1.5 py-0.5 ${r.elite ? 'bg-amber-500/10 text-amber-400 border-amber-500/30' : 'bg-primary/10 text-primary border-primary/30'}`}>
                    {r.soon ? "Pro - Soon" : r.elite ? "Elite" : "Pro"}
                  </Badge>
                )}
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-3">
                  <r.icon className="w-6 h-6 text-primary" />
                </div>
                <h4 className="font-heading font-extrabold text-foreground text-sm uppercase tracking-wide">
                  {r.label}
                </h4>
                {r.isEmail ? (
                  <a href={`mailto:${r.desc}`} className="text-xs text-primary hover:underline mt-1 inline-block" data-testid="pricing-support-email">
                    {r.desc}
                  </a>
                ) : (
                  <p className="text-xs text-muted-foreground mt-1">{r.desc}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Lifetime Discount CTA */}
        <div className="mt-12 max-w-lg mx-auto">
          <Card className="glass-card border-primary/20 overflow-hidden" data-testid="lifetime-discount-cta">
            <CardContent className="p-6 text-center relative">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary via-cyan-400 to-blue-500" />
              <Sparkles className="w-8 h-8 text-primary mx-auto mb-3" />
              <h3 className="font-heading text-lg font-extrabold uppercase text-foreground mb-1">
                Lock In Your Lifetime Discount
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                First 50 coaches get <span className="text-primary font-bold">permanent discounted pricing</span> — don't miss out
              </p>
              <Button className="btn-premium text-white font-extrabold px-8" data-testid="lock-discount-btn">
                <Zap className="w-4 h-4 mr-2" />
                Go Pro Now
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground">
            Tracked with <span className="text-primary font-medium">FitTrack Coach</span>
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            <a href="https://app.fittrackcoach.in" target="_blank" rel="noopener noreferrer" className="hover:text-primary transition-colors">app.fittrackcoach.in</a>
          </p>
          <p className="text-[11px] text-muted-foreground mt-0.5">by InfiNest Tech Pvt. Ltd.</p>
        </div>
      </main>
    </div>
  );
}
